//
// # IDs & ID Maps
//

// Max value for i32, matching the Rust-side Id type (i32)
const INT32_MAX = 2 ** 31 - 1;

// Generate a random ID in [1, INT32_MAX] to avoid collisions
// between independently-running client and server instances.
function randomId(): number {
  return Math.floor(Math.random() * INT32_MAX) + 1;
}

// # ID Map
// Keep track of entities by their ID, and give each a unique ID when added.
// API is modeled after `Set`, which this frequently replaces.
export class IdMap<
  Value extends DataHasId<Data>, // The values in the map
  Data extends HasId = HasId // Data elements which have an ID
> {
  private readonly _map = new Map<number, Value>();
  add = (val: Value): IdMap<Value> => {
    // Preserve existing IDs instead of overwriting.
    // Only assign a new random ID if one is not already set.
    const id = val.data.id ?? randomId();
    val.data.id = id;
    this._map.set(id, val);
    return this;
  };
  get = (id: number): Value | undefined => this._map.get(id);
  remove = (id: number): boolean => this._map.delete(id);
  forEach = (fn: (val: Value) => void): void => this._map.forEach(fn);
  values = () => this._map.values();
}

type HasId = { id?: number };
type DataHasId<Data extends HasId = HasId> = { data: Data };
